/**
 * One instalment before and after a change, side by side, for PaymentDiffTable: Payments →
 * Import history (an applied run) and a carrier read's held policies (a proposed one) show the
 * same table.
 *
 * An entry is `{ amount, status, paidAmount, paidDate }`; null when the instalment did not
 * exist on that side.
 */

/** compareTo semantics, not string equality: 9589.6 and 9589.60 are the same money. */
export function moneyMoved(was, now) {
  if (was === null || was === undefined || now === null || now === undefined) {
    return (was ?? null) !== (now ?? null);
  }
  return Number(was) !== Number(now);
}

const collected = (p) => (p && p.paidAmount ? `${Number(p.paidAmount)}|${p.paidDate || ''}` : '');

/**
 * `pairs`: `{ key, dueDate, before, after }`, in any order. Returns them oldest first, each with
 * `moved` (which fields changed) and `changed` (anything did, or the instalment came or went).
 */
export function diffPairs(pairs) {
  return (pairs || [])
    .map((p) => {
      const b = p.before || null;
      const a = p.after || null;
      const moved = {
        amount: moneyMoved(b?.amount, a?.amount),
        status: (b?.status || null) !== (a?.status || null),
        collected: collected(b) !== collected(a),
      };
      return {
        ...p,
        before: b,
        after: a,
        moved,
        changed: !b || !a || moved.amount || moved.status || moved.collected,
      };
    })
    .sort((x, y) => String(x.dueDate).localeCompare(String(y.dueDate)));
}

/**
 * A held policy's instalment (the engine's `current` or `target` line) as a diff entry. A paid
 * one is shown as collected for its amount: the schedule records the day, not a separate sum.
 */
export function scheduleEntry(line) {
  if (!line) return null;
  const paid = line.status === 'PAID';
  return {
    amount: line.amount,
    status: line.status,
    paidAmount: paid ? (line.paidAmount ?? line.amount) : null,
    paidDate: paid ? line.paidDate || null : null,
  };
}
